import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';

const UpcomingEvents = () => {
  const [events, setEvents] = useState([]);
  const [imgID, setImgID] = useState(0);
  const [disp, setDisp] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [registeredEvents, setRegisteredEvents] = useState([]);
  // State to handle the closing animation
  const [isClosing, setIsClosing] = useState(false);

  // Effect to lock body scroll when modal is open
  useEffect(() => {
    if (showModal) {
      document.body.classList.add('modal-open-body');
    } else {
      document.body.classList.remove('modal-open-body');
    }
    // Cleanup on component unmount
    return () => {
      document.body.classList.remove('modal-open-body');
    };
  }, [showModal]);


  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get("https://ethiccraft.org/backend/upcoming_events/read_isShown.php");
      const data = response.data;

      const filtered = data.map((e) => ({
        ...e,
        title: e.name,
        fullDate: e.date,
        img: `https://ethiccraft.org/${e.banner_image1}`,
        description: e.description,
      }));

      setEvents(filtered);
    } catch (err) {
      setError("Failed to load upcoming events.");
    }
  }, []);

  const fetchRegisteredEvents = useCallback(async () => {
    try {
      const useremail = getCookie('email');
      if (!useremail) return;

      const response = await axios.get(`https://ethiccraft.org/backend/event_bookings/fetch_user_events.php?email=${useremail}`, {
        withCredentials: true
      });

      if (Array.isArray(response.data)) {
        setRegisteredEvents(response.data.map(e => e.event_id));
      }
    } catch (error) {
      console.error("Failed to fetch registered events:", error);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchRegisteredEvents();
  }, [fetchEvents, fetchRegisteredEvents]);

  const toggleOverlay = useCallback((id) => {
    setImgID(id);
    setDisp(true);
  }, []);

  const handleGetTickets = (event) => {
    if (event.isOpen !== "1") {
      Swal.fire({
        icon: 'info',
        title: 'Form Closed',
        text: 'Ticket booking for this event is currently closed.',
      });
      return;
    }
    setSelectedEvent(event);
    setAnswers({});
    setShowModal(true);
  };

  // Function to smoothly close the modal
  const handleCloseModal = () => {
    setIsClosing(true);
  };

  const onAnimationEnd = () => {
    if (isClosing) {
      setShowModal(false);
      setIsClosing(false);
    }
  };
  
  const getCookie = (name) => {
      const cookieString = document.cookie;
      const cookies = cookieString.split(';');
      for (let i = 0; i < cookies.length; i++) {
          let cookie = cookies[i].trim();
          if (cookie.startsWith(name + '=')) {
              return cookie.substring(name.length + 1);
          }
      }
      return null;
  };

  const handleSubmit = async () => {
    const useremail = getCookie('email');

    if (!useremail) {
      Swal.fire({
        icon: 'warning',
        title: 'Login Required',
        text: 'Please log in first to book the event.',
      });
      return;
    }

    for (let i = 1; i <= 30; i++) {
      const isMandatory = selectedEvent[`qmand${i}`] === "1";
      const type = selectedEvent[`questionType${i}`];
      const qKey = `q${i}`;
      const value = answers[qKey];

      if (isMandatory) {
        if (
          value == null ||
          (typeof value === 'string' && value.trim() === '') ||
          (type === "mcq" && (!value || JSON.parse(value).length === 0))
        ) {
          Swal.fire({
            icon: 'error',
            title: 'Incomplete Form',
            text: `Please answer mandatory Question ${i} before submitting.`,
          });
          return;
        }
      }
    }

    setLoading(true);

    try {
      const formattedAnswers = {};
      for (let i = 1; i <= 30; i++) {
        const qKey = `q${i}`;
        formattedAnswers[`answer${i}`] = answers[qKey] ?? null;
      }

      const payload = {
        event_name: selectedEvent.name,
        whatsapp_link: selectedEvent.whatsapp_link,
        event_id: selectedEvent.id,
        ...formattedAnswers
      };

      const response = await fetch("https://ethiccraft.org/backend/event_bookings/upload.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify(payload)
      });

      const text = await response.text();
      let json = {};
      try {
        json = JSON.parse(text);
      } catch (e) {
        throw new Error(`Response was not valid JSON: ${text}`);
      }

      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: json.message || "Booking submitted successfully!",
        }).then(() => {
          handleCloseModal();
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: json.error || "Failed to book the event.",
        });
      }

    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.message || "An unexpected error occurred.",
      });
    } finally {
      setLoading(false);
    }
  };

  const renderQuestions = () => {
    const questions = [];

    for (let i = 1; i <= 30; i++) {
      const qKey = `question${i}`;
      const typeKey = `questionType${i}`;
      let question = selectedEvent[qKey];
      const type = selectedEvent[typeKey];

      if (!question) continue;

      const name = `q${i}`;
      const isMandatory = selectedEvent[`qmand${i}`] === "1";
      const optionRegex = /{(.*?)}/;
      const match = question.match(optionRegex);
      const options = match ? match[1].split(",").map(opt => opt.trim().replace(/^"|"$/g, "")) : [];
      const questionText = match ? question.replace(optionRegex, "").trim() : question;

      questions.push(
        <div
          key={i}
          className="mb-4 pb-3 border-bottom question-item"
          style={{ animationDelay: `${i * 60}ms` }}
        >
          <label className="Ticket_Question">
            <strong>
              Q{i}: {questionText}
              {isMandatory && <span style={{ color: 'red' }}> *</span>}
            </strong>
          </label>

          {type === "text" && (
            <div className="mt-2" style={{ marginLeft: "1.5rem" }}>
              <input
                type="text"
                className="form-control"
                placeholder="Your answer"
                value={answers[name] || ""}
                onChange={(e) => setAnswers({ ...answers, [name]: e.target.value })}
              />
            </div>
          )}

          {type === "radio" && options.length > 0 && (
            <div className="d-flex flex-column gap-2 mt-2" style={{ marginLeft: "1.5rem" }}>
              {options.map((opt, idx) => (
                <div className="form-check" key={idx} style={{ display: "flex", alignItems: "center" }}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name={name}
                    id={`${name}_${idx}`}
                    value={opt}
                    checked={answers[name] === opt}
                    onChange={(e) => setAnswers({ ...answers, [name]: e.target.value })}
                    style={{ marginRight: "0.5rem" }}
                  />
                  <label className="form-check-label" htmlFor={`${name}_${idx}`}>
                    {opt}
                  </label>
                </div>
              ))}
            </div>
          )}

          {type === "rating" && (
            <div className="d-flex gap-2" style={{ marginLeft: "1.5rem" }}>
              {[1, 2, 3, 4, 5].map((val) => (
                <span
                  key={val}
                  role="button"
                  style={{
                    cursor: "pointer",
                    fontSize: "1.5rem",
                    color: answers[name] >= val ? "gold" : "#ccc"
                  }}
                  onClick={() => setAnswers({ ...answers, [name]: val })}
                >
                  ★
                </span>
              ))}
            </div>
          )}

          {type === "mcq" && options.length > 0 && (
            <div className="d-flex flex-column gap-2 mt-2" style={{ marginLeft: "1.5rem" }}>
              {options.map((opt, idx) => {
                const current = answers[name] ? JSON.parse(answers[name]) : [];
                const isChecked = current.includes(opt);
                return (
                  <div className="form-check" key={idx} style={{ display: "flex", alignItems: "center" }}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`${name}_mcq_${idx}`}
                      checked={isChecked}
                      onChange={() => {
                        let updated = [...current];
                        if (isChecked) {
                          updated = updated.filter(item => item !== opt);
                        } else {
                          updated.push(opt);
                        }
                        setAnswers({ ...answers, [name]: JSON.stringify(updated) });
                      }}
                      style={{ marginRight: "0.5rem" }}
                    />
                    <label className="form-check-label" htmlFor={`${name}_mcq_${idx}`}>
                      {opt}
                    </label>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    return questions;
  };

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  if (events.length === 0 && !error) {
    return <div className="alert alert-info">Loading upcoming events...</div>;
  }
  
  return (
    <section className="upcoming-events">
      {disp && (
        <div className="overlay show" onClick={() => setDisp(false)}>
          <img
            src={events[imgID]?.img}
            alt="Event Banner"
            className="full-img fade-in"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <div className="heading-wrapper">
        <h2>
          <span className="upcoming-title">UPCOMING</span> <span className="fest-title">EVENTS</span>
        </h2>
        <div className="header-underline"></div>
      </div>

      <div className="three-cards">
        <div className="row">
          {events.map((event, index) => (
            <div className="col-lg-4 col-md-6 col-sm-12 card-padding" key={index} style={{ "--i": index + 1 }}>
              <div className="card">
                <div className="card-img">
                  <img
                    src={event.img}
                    alt={`${event.title} Banner`}
                    className="event-img"
                    onClick={() => toggleOverlay(index)}
                  />
                </div>
                <div className="card-text">
                  <p className="event-name">{event.title}</p>
                  <div className="small-text">
                    <p><strong>Date:</strong> {event.fullDate}</p>
                    <p><strong>Venue:</strong> {event.venue}</p>
                    <div className="button-group">
                      {registeredEvents.includes(event.id) ? (
                        <button className="btn btn-success" disabled>
                          Registered
                        </button>
                      ) : (
                        <button className="Rd get-tickets-btn" onClick={() => handleGetTickets(event)}>
                          <b>Get Tickets</b>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showModal && selectedEvent && (
        <div className={`modal-overlay99 ${isClosing ? 'closing' : ''}`} onClick={handleCloseModal}>
          <div
            className={`Upcoming_Event_modal-content ${isClosing ? 'closing' : ''}`}
            onClick={(e) => e.stopPropagation()}
            onAnimationEnd={onAnimationEnd}
          >
            <button className="modal-close-btn" onClick={handleCloseModal} aria-label="Close modal">
              &times;
            </button>
            
            {selectedEvent.img && (
              <img
                src={selectedEvent.img}
                alt={`${selectedEvent.title} banner`}
                className='modal-image'
              />
            )}

            <h3 className='selectedEvent_title'>{selectedEvent.title}</h3>
            <p className='selectedEvent_content'><strong>Date:</strong> {selectedEvent.fullDate}</p>
            <p className='selectedEvent_content'><strong>Venue:</strong> {selectedEvent.venue}</p>
            {selectedEvent.college !== 'main' && (
              <p className='selectedEvent_content'>
                <strong>College:</strong> {selectedEvent.college}
              </p>
            )}
            <p className='selectedEvent_content'><strong>Description:</strong> {selectedEvent.description}</p>
            <hr />
            
            <form>
              {renderQuestions()}
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary mt-3"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default UpcomingEvents;